/**
 * useSignoffTeamLists — signoff dropdowns + per-cell gating for the WO grids.
 *
 * RBAC overhaul Phase C (qa/rbac-overhaul-2026-08-06/PLAN.md): review is
 * SKILL-based. Each WO detail row arrives annotated with ReviewSkillKeys /
 * ReviewSkillNames (backend testSkillService), and:
 *   - Tested By stays role-gated (technician+), options = ?signoff=check.
 *   - Reviewed By / Review Date are editable per ROW: admins always;
 *     otherwise only when the caller holds skill level >= 4 in one of the
 *     row's skills. Locked cells show a
 *     tooltip naming the missing skill. The Reviewed By dropdown offers ONLY
 *     the signed-in user, and only when they are a level-4+ holder of the
 *     row's skill (Sam 2026-09-29 — a reviewer records their own review;
 *     nobody picks a colleague). The skill-scoped level-4+ list
 *     (?signoff=review&wod=…) is still fetched and is what the self check
 *     runs against.
 *   - Approved By / Approval Date are ADMIN-only (recorded on behalf of the
 *     out-of-system approver); the name list is the row's level-9 approver
 *     designates (?signoff=approve&wod=…).
 * Row lists are fetched once per distinct skill signature, not per row.
 * Mirrors the server gates (validateSignoffPermissions +
 * enforceReviewSkillGate), so the 403 can never be reached from the grid.
 */
import { useCallback, useState } from 'react';
import type { GridColumn } from '../components/tables/EditableGrid';
import {
  getTeamMembers, getMySkillLevels, getSignoffOptionsForWod,
  type WodSignoffOptions, type SignoffOption,
} from '../services/workOrderService';
import { usePermissions } from './usePermissions';
import { REVIEWER_MIN_SKILL_LEVEL } from '../constants/scheduleEnums';

const CHECK_FIELDS = new Set(['CheckedBy', 'TestDate']);
const REVIEW_FIELDS = new Set(['ReviewedBy', 'ReviewDate']);
const APPROVE_FIELDS = new Set(['ApprovedBy', 'ApprovalDate']);

type Row = Record<string, unknown>;

/** Stable signature for a row's skill set — the cache key for row lists. */
function skillSig(row: Row): string {
  const keys = row.ReviewSkillKeys;
  return Array.isArray(keys) ? [...(keys as string[])].sort().join(',') : '';
}

/** TestStatus values whose DIRECT selection is gated server-side: 'Approved'
 *  is admin-only; 'Review Completed' needs the row's review skill (or admin).
 *  They normally arrive via the ApprovedBy/ReviewedBy auto-promotion. */
const ADMIN_ONLY_STATUSES = new Set(['Approved']);
const REVIEW_STATUS = 'Review Completed';

export function useSignoffTeamLists(currentUserName?: string | null) {
  const { canCheckTest, isAdmin, canChangeTestStatus } = usePermissions();
  const [checkerNames, setCheckerNames] = useState<string[]>([]);
  const [approverNames, setApproverNames] = useState<string[]>([]);
  const [mySkillLevels, setMySkillLevels] = useState<Record<string, number>>({});
  // Rich per-signature lists: each candidate carries their level in the
  // row's skill, rendered with SkillLevelChip (scenario-picker idiom).
  const [richBySig, setRichBySig] = useState<Record<string, WodSignoffOptions>>({});

  const loadTeamLists = useCallback(async () => {
    try {
      const [checkers, approvers, skills] = await Promise.all([
        getTeamMembers('check'),
        getTeamMembers('approve'),
        getMySkillLevels().catch(() => []),
      ]);
      const promoteSelf = (names: string[]) =>
        currentUserName && names.includes(currentUserName)
          ? [currentUserName, ...names.filter((n) => n !== currentUserName)]
          : names;
      setCheckerNames(promoteSelf(checkers));
      setApproverNames(promoteSelf(approvers));
      setMySkillLevels(Object.fromEntries(skills.map((s) => [s.SkillKey, s.Level])));
    } catch { /* best effort — dropdowns fall back to static defaults */ }
  }, [currentUserName]);

  /**
   * Fetch the skill-scoped Reviewed By / Approved By lists for a set of grid
   * rows — one pair of requests per DISTINCT skill signature (a WO rarely has
   * more than a handful of distinct tests). Call after the details load.
   */
  const loadRowSignoffLists = useCallback(async (rows: Row[]) => {
    try {
      const repBySig = new Map<string, string>();
      for (const r of rows) {
        const sig = skillSig(r);
        const key = r.WorkOrderDetailKey as string | undefined;
        if (key && !repBySig.has(sig)) repBySig.set(sig, key);
      }
      const entries = [...repBySig.entries()];
      const results = await Promise.all(entries.map(async ([sig, wodKey]) => ({
        sig,
        options: await getSignoffOptionsForWod(wodKey),
      })));
      setRichBySig(Object.fromEntries(results.map((r) => [r.sig, r.options])));
    } catch { /* best effort — falls back to the legacy role-based lists */ }
  }, []);

  /**
   * Rich options for one dropdown of one row, self promoted to the top.
   * `strict` (Reviewed By): once the row's lists have loaded, return the list
   * EVEN WHEN EMPTY — an unqualified-for-everyone test must show an empty
   * dropdown, never fall back to a role-based list (Vahid 2026-08-06: the
   * reviewer list is exactly the level-4+ holders of the row's skill).
   * Non-strict (checkers/designates): empty → undefined so the legacy
   * fallback options apply.
   */
  const richFor = useCallback((row: Row, kind: keyof WodSignoffOptions, strict = false) => {
    const loaded = richBySig[skillSig(row)];
    if (!loaded) return undefined; // not fetched (yet/failed) — fallback allowed
    const list = loaded[kind];
    if (list.length === 0) return strict ? [] : undefined;
    const toItem = (o: SignoffOption) => ({ value: o.name, level: o.level });
    const self = currentUserName ? list.find((o) => o.name === currentUserName) : undefined;
    return self
      ? [toItem(self), ...list.filter((o) => o.name !== currentUserName).map(toItem)]
      : list.map(toItem);
  }, [richBySig, currentUserName]);

  /** Why the caller can't review this row — or false when they can. */
  const rowReviewLock = useCallback((row: Row): string | false => {
    if (isAdmin) return false; // admin/superadmin bypass
    const keys = (row.ReviewSkillKeys as string[] | undefined) ?? [];
    if (keys.length === 0) {
      return 'This test has no associated skill — only a Lab Admin can review it.';
    }
    const qualified = keys.some((k) => (mySkillLevels[k] ?? 0) >= REVIEWER_MIN_SKILL_LEVEL);
    if (qualified) return false;
    const names = (row.ReviewSkillNames as string[] | undefined)?.join(', ');
    return `Reviewing this test requires skill level ${REVIEWER_MIN_SKILL_LEVEL}+ in: ${names || 'its skill'}.`;
  }, [isAdmin, mySkillLevels]);

  const decorateSignoffColumns = useCallback((c: GridColumn): GridColumn => {
    if (c.field === 'TestStatus') {
      if (isAdmin) return c;
      if (!canChangeTestStatus) return { ...c, readonly: true };
      // Mirror the server: hide statuses this caller can't set on this row
      // (validateTechnicianFieldScope + enforceReviewSkillGate).
      return {
        ...c,
        getOptionsForRow: (row: Row) => {
          const base = (c.options ?? []) as string[];
          return base.filter((s) =>
            !ADMIN_ONLY_STATUSES.has(s)
            && (s !== REVIEW_STATUS || rowReviewLock(row) === false));
        },
      };
    }
    if (CHECK_FIELDS.has(c.field)) {
      const col = c.field === 'CheckedBy'
        ? {
          ...c,
          ...(checkerNames.length > 0 ? { options: checkerNames } : {}),
          getRichOptionsForRow: (row: Row) => richFor(row, 'checkers'),
        }
        : c;
      return canCheckTest ? col : { ...col, readonly: true };
    }
    if (REVIEW_FIELDS.has(c.field)) {
      if (!canCheckTest) return { ...c, readonly: true }; // readers never write
      return {
        ...c,
        getCellReadonly: rowReviewLock,
        ...(c.field === 'ReviewedBy' ? {
          // SELF-ONLY (Sam 2026-09-29): a reviewer records their OWN review.
          // The dropdown offers the signed-in user alone — and only when they
          // are a level-4+ holder of the row's skill (i.e. in the strict
          // reviewers list). Anyone else gets an empty dropdown; the
          // unqualified are additionally locked out by rowReviewLock above.
          // Never falls back to the role-based team list.
          getRichOptionsForRow: (row: Row) =>
            (richFor(row, 'reviewers', true) ?? []).filter((o) => o.value === currentUserName),
        } : {}),
      };
    }
    if (APPROVE_FIELDS.has(c.field)) {
      if (!isAdmin) {
        return {
          ...c,
          readonly: false,
          getCellReadonly: () =>
            'Approvals are recorded by a Lab Admin on behalf of the approver designate.',
        };
      }
      return c.field === 'ApprovedBy'
        ? {
          ...c,
          getRichOptionsForRow: (row: Row) => richFor(row, 'designates'),
          getOptionsForRow: () => approverNames, // fallback when rich fetch failed
        }
        : c;
    }
    return c;
  }, [checkerNames, approverNames, richFor, currentUserName,
    canCheckTest, isAdmin, canChangeTestStatus, rowReviewLock]);

  return {
    checkerNames, approverNames, loadTeamLists, loadRowSignoffLists,
    decorateSignoffColumns, mySkillLevels,
  };
}
